
import React, { useEffect } from 'react';
import update from 'immutability-helper';

import AddressLookupField from 'views/AddressLookupField.js';
import Appearance from 'styles/Appearance.js';
import { AsyncInteractionItem } from 'managers/Components.js';
import ContactPicker from 'views/ContactPicker.js';
import DatePickerDropdownField from 'views/DatePickerDropdownField.js';
import DateRangePickerField from 'views/DateRangePickerField.js';
import DataVaultItemsPickerField from 'views/DataVaultItemsPickerField.js';
import DurationPicker from 'views/DurationPicker.js';
import EmailEditor from 'views/EmailEditor.js';
import FilePickerField from 'views/FilePickerField.js';
import HourPicker from 'views/HourPicker.js';
import ImagePickerField from 'views/ImagePickerField.js';
import InsetLabelTextField from 'views/InsetLabelTextField.js';
import InstructionsList from 'views/InstructionsList.js';
import { LayerItem } from 'structure/Layer.js';
import LegacyMessageEditor from 'views/LegacyMessageEditor.js';
import ListField from 'views/ListField.js';
import LocationRadiusLookupField from 'views/LocationRadiusLookupField.js';
import MultipleListField from 'views/MultipleListField.js';
import MultipleTextField from 'views/MultipleTextField.js';
import ScopeEditor from 'views/ScopeEditor.js';
import TextField from 'views/TextField.js';
import TimePicker from 'views/TimePicker.js';
import TrustedContactList from 'views/TrustedContactsList';
import UserLookup from 'views/UserLookup.js';
import { useDataStore } from 'files/StateManagement';

export const validateRequiredFields = async get => {
    return new Promise((resolve, reject) => {
        let items = get().reduce((array, section) => {
            if(section.authentication?.required === true && section.authentication?.completed === false) {
                return array;
            }
            return section.visible !== false ? array.concat(section.items) : array;
        }, []);
        let required = items.find(item => {
            if(item.visible === false || item.required === false || item.component === 'custom') {
                return false;
            }
            return item.value === null || item.value === undefined;
        });
        if(required) {
            let error = new Error(`One or more required fields are incomplete. Please fill out the "${required.title.toLowerCase()}" field before moving on`);
            reject(error);
            return;
        }
        resolve();
    })
}

const AltFieldMapper = ({ fields, resourceID, utils }) => {

    const { requiem, setRequiem } = useDataStore();

    const onFocus = (item, force = false) => {

    }

    const onRequiemSmartFlowClick = item => {
        if(item.key && requiemSmartFlowEnabled() === true) {
            setRequiem(requiem => {
                return update(requiem, {
                    request: {
                        $set: {
                            action: 'smart_flow_event',
                            event: 'item_click',
                            item_key: item.key,
                            smart_flow: {
                                ...requiem.smart_flow,
                                active_item_key: item.key
                            }
                        }
                    }
                })
            });
        }
    }

    const onUpdateRequiemSmartFlowFields = () => {
        if(requiemSmartFlowEnabled() === true) {
            setRequiem(requiem => {
                return update(requiem, {
                    smart_flow: {
                        items: {
                            $set: getSections()
                        }
                    }
                })
            });
        }
    }

    const getFields = () => {
        return getSections().reduce((array, section) => {
            return array.concat(section.items.filter(item => item.visible !== false));
        }, []);
    }

    const getLayerItemContent = section => {

        let padding = 16;
        if(section.authentication?.required === true && section.authentication?.completed === false) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'This area is locked behind additional authentication. Click here to authenticate.'}
                    image={'images/prohibitory-red-small.png'}
                    lastItem={true}
                    onAsyncClick={section.authentication.onAsyncClick}
                    title={'Authentication Required'}
                    utils={utils} />
                </div>
            )
        }
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                overflow: 'hidden',
                width: '100%',
                ...section.style
            }}>
                {section.header}
                <div style={{
                    paddingBottom: padding,
                    paddingLeft: padding,
                    paddingRight: padding,
                    paddingTop: 12,
                    width: '100%'
                }}>
                    {section.content}
                    {typeof(section.description) === 'string' && (
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            display: 'block',
                            marginBottom: 24
                        }}>{section.description}</span>
                    )}
                    {section.items && section.items.filter(item => {
                        return item.visible !== false;
                    }).map((item, index, items) => {
                        return (
                            <AltFieldItem 
                            item={item} 
                            key={index} 
                            lastItem={typeof(item.lastItem) === 'boolean' ? item.lastItem : index === items.length - 1} 
                            onFocus={onFocus.bind(this, item)}
                            onRequiemSmartFlowClick={onRequiemSmartFlowClick.bind(this, item, true)}
                            requiemSmartFlowEnabled={requiemSmartFlowEnabled() === true && item?.requiemSmartFlow === true}
                            utils={utils} />
                        )
                    })}
                </div>
            </div>
        )
    }

    const getSections = () => {
        return fields.filter(section => section.visible !== false);
    }

    const requiemSmartFlowEnabled = () => {
        return resourceID && resourceID === requiem.smart_flow?.id && requiem.smart_flow.enabled === true ? true : false;
    }

    useEffect(() => {
        onUpdateRequiemSmartFlowFields();
    }, [getFields().length]);

    return getSections().map((section, index, sections) => {
        return (
            <LayerItem
            collapsed={section.collapsed !== undefined ? section.collapsed : false}
            key={index}
            lastItem={typeof(section.lastItem) === 'boolean' ? section.lastItem : (index === sections.length - 1)}
            rightContent={section.rightContent}
            title={section.title}
            useStyle={false}>
                {getLayerItemContent(section)}
            </LayerItem>
        )
    });
}

export const AltFieldItem = ({ item, lastItem = true, onFocus, onRequiemSmartFlowClick, requiemSmartFlowEnabled = false, utils }) => {

    const getComponent = item => {

        let { component, icon, items, loading, onChange, onClick, props = {}, requiemSmartFlow, value } = item;
        switch(component) {

            case 'address_lookup':
            return (
                <AddressLookupField
                {...item}
                geocode={true}
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'bool_list':
            return (
                <ListField
                autoOpenOnFocus={requiemSmartFlow === true}
                items={[{
                    id: 'yes',
                    title: 'Yes'
                },{
                    id: 'no',
                    title: 'No'
                }]}
                onChange={item => onChange(item ? item.id === 'yes' : null)}
                onFocus={onFocus}
                utils={utils}
                value={typeof(value) === 'boolean' ? (value ? 'Yes' : 'No') : null}
                {...props} />
            );

            case 'contact_picker':
            return (
                <ContactPicker
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'custom':
            return value;

            case 'date_picker':
            return (
                <DatePickerDropdownField
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'date_range_picker':
            return (
                <DateRangePickerField
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'data_vault_items_picker':
            return (
                <DataVaultItemsPickerField 
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                values={value}
                {...props} />
            )

            case 'duration_picker':
            return (
                <DurationPicker
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'email_editor':
            return (
                <EmailEditor
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'file_picker':
            return (
                <FilePickerField
                onChange={onChange}
                onFocus={onFocus}
                value={value}
                {...props} />
            )

            case 'hour_picker': 
            return (
                <HourPicker
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'image_picker':
            return (
                <ImagePickerField
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'inset_label_textfield':
            return (
                <InsetLabelTextField
                icon={icon}
                insetLabel={item.title}
                loading={loading}
                onChange={onChange}
                onClick={onClick}
                onFocus={onFocus}
                value={value}
                {...props} />
            )

            case 'instructions_list':
            return (
                <InstructionsList
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'legacy_message_editor':
            return (
                <LegacyMessageEditor
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'list':
            return (
                <ListField
                autoOpenOnFocus={requiemSmartFlow === true}
                items={items}
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'location_radius_lookup':
            return (
                <LocationRadiusLookupField
                utils={utils}
                value={value}
                onChange={onChange}
                onFocus={onFocus}
                {...props} />
            )

            case 'multiple_list':
            return (
                <MultipleListField
                items={items}
                onChange={onChange}
                onFocus={onFocus}
                value={value}
                {...props} />
            )

            case 'multiple_textfield':
            return (
                <MultipleTextField
                onChange={onChange}
                onFocus={onFocus}
                value={value}
                {...props} />
            )

            case 'number_input':
            return (
                <TextField
                format={'number'}
                icon={icon}
                loading={loading}
                onChange={onChange}
                onFocus={onFocus}
                onClick={onClick}
                value={value}
                {...props} />
            )

            case 'scope_editor':
            return (
                <ScopeEditor
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'textfield':
            return (
                <TextField
                icon={icon}
                loading={loading}
                onChange={onChange}
                onClick={onClick}
                onFocus={onFocus}
                requiem={requiemSmartFlow === true}
                value={value}
                {...props} />
            )

            case 'textview':
            return (
                <TextField
                icon={icon}
                value={value}
                loading={loading}
                expandWithText={true}
                onChange={onChange}
                onFocus={onFocus}
                {...props} />
            )

            case 'time_picker':
            return (
                <TimePicker
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'trusted_contacts_list':
            return (
                <TrustedContactList
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            case 'user_lookup':
            return (
                <UserLookup
                onChange={onChange}
                onFocus={onFocus}
                utils={utils}
                value={value}
                {...props} />
            )

            default:
            return null;
        }
    }

    const itemSupportsTitle = () => {
        return item.useTitle === false || [item.component].includes('email_editor') ? false : true;
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: lastItem === false ? 24 : 0,
            width: '100%'
        }}>
            {item.header}
            {itemSupportsTitle() === true && (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    marginBottom: 4
                }}>
                    {item.required !== false && (item.value === null || item.value === undefined) && (
                        <div style={{
                            width: 7.5,
                            height: 7.5,
                            borderRadius: 3.75,
                            overflow: 'hidden',
                            backgroundColor: Appearance.colors.red,
                            marginRight: 8,
                            minWidth: 5
                        }} />
                    )}
                    <span 
                    className={requiemSmartFlowEnabled === true ? `requiem-shimmer-text ${window.theme}` : ''}
                    onClick={onRequiemSmartFlowClick}
                    title={requiemSmartFlowEnabled === true ? `Click to have Requiem explain ${item.title}` : item.title}
                    style={{
                        ...Appearance.textStyles.subHeader(),
                        ...requiemSmartFlowEnabled === true && {
                            color: 'transparent'
                        },
                        display: 'block',
                        whiteSpace: 'normal'
                    }}>{item.title}</span>
                </div>
            )}
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                position: 'relative',
                width: '100%'
            }}>
                {getComponent(item)}
            </div>
            {item.bottomContent}
        </div>
    )
}

export default AltFieldMapper;
